import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface MessageActionsProps {
  isOpen: boolean;
  onClose: () => void;
  onReply: () => void;
  onReact: () => void;
  isOutgoing: boolean;
  position: { top: number; left: number };
  onAiReply?: () => void;
  aiReplyLoading?: boolean;
  onCopy?: () => void;
}

export default function MessageActions({
  isOpen,
  onClose,
  onReply,
  onReact,
  isOutgoing,
  position,
  onAiReply,
  aiReplyLoading = false,
  onCopy,
}: MessageActionsProps) {
  const { t } = useTranslation("dashboard");
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-[99] bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-1 min-w-[140px] animate-in fade-in zoom-in-95 duration-150"
      style={{ top: `${position.top}px`, left: `${position.left}px` }}
    >
      <button
        onClick={() => {
          onReply();
          onClose();
        }}
        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
      >
        <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200/60 text-foreground-500">
          <i className="ri-reply-line text-sm"></i>
        </span>
        {t("actions.reply")}
      </button>
      {onCopy && (
        <button
          onClick={() => {
            onCopy();
            onClose();
          }}
          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
        >
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200/60 text-foreground-500">
            <i className="ri-file-copy-line text-sm"></i>
          </span>
          {t("actions.copy") || "Copy Text"}
        </button>
      )}
      <button
        onClick={() => {
          onReact();
          onClose();
        }}
        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
      >
        <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200/60 text-yellow-500">
          <i className="ri-emotion-line text-sm"></i>
        </span>
        {t("actions.react")}
      </button>
      {!isOutgoing && onAiReply && (
        <button
          onClick={() => {
            onAiReply();
            onClose();
          }}
          disabled={aiReplyLoading}
          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed border-t border-background-200/50 mt-0.5 pt-2"
        >
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            {aiReplyLoading ? (
              <i className="ri-loader-4-line animate-spin text-sm"></i>
            ) : (
              <i className="ri-robot-2-line text-sm"></i>
            )}
          </span>
          {t("actions.aiReply")}
        </button>
      )}
    </div>
  );
}